import { useMemo } from 'react';
import { AgentMarketPlaceItemWithFilter } from '../../../pages/userAgent/agentMarketPlace/AgentMarketPlace';

export const UseLeaderboardStatistics = (agentList: AgentMarketPlaceItemWithFilter[]) => {
  const summaryStats = useMemo(() => {
    const publisherNames: string[] = [];
    let totalDownloads = 0;

    agentList.forEach((agent) => {
      if (agent.owners && agent.owners.length > 0) {
        agent.owners.forEach((owner) => {
          if (owner.name && !publisherNames.includes(owner.name)) {
            publisherNames.push(owner.name);
          }
        });
      }
      totalDownloads += Number(agent.download_count);
    });

    return [
      { title: 'Total Publishers', value: publisherNames.length },
      // { title: 'Active Users', value: 'N/A' },
      { title: 'Available Agents', value: agentList.length },
      { title: 'Total Downloads', value: totalDownloads },
    ];
  }, [agentList]);

  const domainBreakdownList = useMemo(() => {
    if (!agentList || agentList.length === 0) {
      return [];
    }
    // Count agents by category
    const agentscategoryCount: Record<string, number> = {};
    agentList.forEach((agent) => {
      agentscategoryCount[agent.category] = (agentscategoryCount[agent.category] || 0) + 1;
    });

    const domainBreakdownData = Object.entries(agentscategoryCount)
      .map(([domain, count]) => ({
        domain,
        count,
        percentage: Number(((count / agentList.length) * 100).toFixed(2)),
      }))
      .sort((a, b) => b.count - a.count);

    return domainBreakdownData;
  }, [agentList]);

  const rolesSupportedList = useMemo(() => {
    if (!agentList || agentList.length === 0) {
      return [];
    }

    // Count agents by role
    const agentsRoleCount: Record<string, number> = {};
    agentList.forEach((agent) => {
      agentsRoleCount[agent.role] = (agentsRoleCount[agent.role] || 0) + 1;
    });

    const rolesSupportedData = Object.entries(agentsRoleCount)
      .map(([role, count]) => ({
        role,
        count,
        percentage: Number(((count / agentList.length) * 100).toFixed(2)),
      }))
      .sort((a, b) => b.count - a.count);

    return rolesSupportedData;
  }, [agentList]);

  const cloudProvidersList = useMemo(() => {
    if (!agentList || agentList.length === 0) {
      return [];
    }
    // Count agents by cloud provider
    const agentsCloudProvidersCount: Record<string, number> = {};
    agentList.forEach((agent) => {
      agentsCloudProvidersCount[agent.cloud_provider] = (agentsCloudProvidersCount[agent.cloud_provider] || 0) + 1;
    });

    const cloudProvidersData = Object.entries(agentsCloudProvidersCount)
      .map(([cloudProvider, count]) => ({
        cloudProvider,
        count,
        percentage: Number(((count / agentList.length) * 100).toFixed(2)),
      }))
      .sort((a, b) => b.count - a.count);

    return cloudProvidersData;
  }, [agentList]);

  const topRatedAgents = useMemo(() => {
    if (!agentList || agentList.length === 0) {
      return [];
    }

    return [...agentList]
      .filter((agent) => agent.average_rating > 0)
      .sort((a, b) => b.average_rating - a.average_rating)
      .slice(0, 5)
      .map((agent) => ({
        name: agent.name,
        author: agent.owners[0]?.name,
        rating: agent.average_rating,
        downloads: agent.download_count,
      }));
  }, [agentList]);

  const topDownloadedAgents = useMemo(() => {
    if (!agentList || agentList.length === 0) {
      return [];
    }

    return [...agentList]
      .filter((agent) => Number(agent.download_count) > 0)
      .sort((a, b) => Number(b.download_count) - Number(a.download_count))
      .slice(0, 5)
      .map((agent) => ({
        name: agent.name,
        author: agent.owners[0]?.name,
        downloads: agent.download_count,
        rating: agent.average_rating,
      }));
  }, [agentList]);

  const topPublishers = useMemo(() => {
    if (!agentList || agentList.length === 0) {
      return [];
    }

    const publisherCounts: Record<string, number> = {};

    agentList.forEach((agent) => {
      if (agent.owners && agent.owners.length > 0) {
        // Get all valid owner names for this agent
        const validOwners = agent.owners.filter((owner) => owner.name).map((owner) => owner.name);

        if (validOwners.length > 0) {
          // If multiple owners, create combined name; if single owner, use individual name
          const publisherName = validOwners.length > 1 ? validOwners.join(' & ') : validOwners[0];

          publisherCounts[publisherName] = (publisherCounts[publisherName] || 0) + 1;
        }
      }
    });

    return Object.entries(publisherCounts)
      .map(([name, agentsCount]) => ({ name, agentsCount }))
      .sort((a, b) => b.agentsCount - a.agentsCount)
      .slice(0, 5);
  }, [agentList]);

  return {
    summaryStats,
    domainBreakdownList,
    rolesSupportedList,
    cloudProvidersList,
    topRatedAgents,
    topDownloadedAgents,
    topPublishers,
  };
};
